import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import UnauthorizedLayout from '../UnauthorizedLayout'
import SignupStepIndicator from './SignupStepIndicator'
import Step1UserType from './Step1UserType'
import Step2ModuleSelection from './Step2ModuleSelection'
import Step3PersonalInfo from './Step3PersonalInfo'
import AuthService from '../../../services/AuthService'
import useNotification from '../../../hooks/useNotification'

/**
 * MultiStepSignup Component
 *
 * Manages the multi-step signup flow:
 * 1. User type selection
 * 2. Module selection
 * 3. Personal information form
 */
const MultiStepSignup = () => {
  const { t } = useTranslation('auth')
  const [currentStep, setCurrentStep] = useState(1)
  const [userType, setUserType] = useState('')
  const [selectedModules, setSelectedModules] = useState([])

  const { info, error: showError } = useNotification()
  const navigate = useNavigate()

  const handleUserTypeSelect = (type) => {
    setUserType(type)
  }

  const handleStep1Next = () => {
    if (!userType) {
      showError(t('signup.toast.selectUserType'))
      return
    }
    setCurrentStep(2)
  }

  const handleModulesChange = (modules) => {
    setSelectedModules(modules)
  }

  const handleStep2Next = () => {
    setCurrentStep(3)
  }

  const handleStep2Back = () => {
    setCurrentStep(1)
  }

  const handleStep3Back = () => {
    setCurrentStep(2)
  }

  const handleFinalSubmit = async (formData) => {
    try {
      const response = await AuthService.signup(formData)
      const resData = response.data

      // For UX and to avoid account enumeration, always show success page
      // regardless of response body when signup is accepted for processing.
      if (resData === 'Administrator was added') {
        info(t('signup.toast.adminAdded'))
        navigate('/login')
        return
      }
      // Standard path for normal users
      navigate('/signup-success')
    } catch (e) {
      console.error('Registration failed:', e?.response?.data)
      const status = e?.response?.status
      const msg =
        e?.response?.data?.error ||
        e?.response?.data?.message ||
        e?.response?.data?.detail ||
        ''

      // If user already exists or similar, show same success page to keep UX simple
      if (
        status === 409 ||
        (status === 400 &&
          /already\s*exists|already\s*registered|user\s*exists/i.test(msg))
      ) {
        navigate('/signup-success')
        return
      }

      // Generic network/server error: keep toaster for transparency
      const errorMessage = msg || t('signup.toast.registrationFailed')
      showError(errorMessage)
    }
  }

  const backToLogin = () => {
    navigate('/login')
  }

  return (
    <UnauthorizedLayout
      title={currentStep === 3 ? undefined : t('signup.title')}
      subtitle={currentStep === 3 ? undefined : t('signup.subtitle')}
      question={t('signup.question')}
      text={t('signup.loginLink')}
      hancleClick={backToLogin}
    >
      <div className={`w-full ${currentStep === 3 ? 'flex flex-col flex-1 min-h-0' : ''}`}>
        <SignupStepIndicator currentStep={currentStep} totalSteps={3} />

        {currentStep === 1 && (
          <Step1UserType
            selectedType={userType}
            onSelect={handleUserTypeSelect}
            onNext={handleStep1Next}
          />
        )}

        {currentStep === 2 && (
          <Step2ModuleSelection
            userType={userType}
            selectedModules={selectedModules}
            onModulesChange={handleModulesChange}
            onNext={handleStep2Next}
            onBack={handleStep2Back}
          />
        )}

        {currentStep === 3 && (
          <div className="flex-1 flex flex-col min-h-0">
          <Step3PersonalInfo
            userType={userType}
            selectedModules={selectedModules}
            onSubmit={handleFinalSubmit}
            onBack={handleStep3Back}
          />
          </div>
        )}
      </div>
    </UnauthorizedLayout>
  )
}

export default MultiStepSignup
